import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Col, Radio, Row, Select, Input } from "antd";
import { GlobalTable } from "../../../global/components/GlobalTable";
import useGet from "../../../global/hooks/useGet";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import SortIcon from "../../../global/components/SortIcon";
import { TERM_LABELS, TERM_OPTIONS } from "../../../utils/Common";
import { isLimitedLicenseRunning } from "../../../utils/limitedLicense";

const LICENSE_ITEM_OPTIONS = [{ label: "Mobile Phone", value: 207 }];
const LICENSE_ITEM_LABELS = {
  207: "Mobile Phone",
};

const initialFilterState = {
  LimitedCardName: "",
  LicenseItem: undefined,
  LicenseCode: "",
  Term: undefined,
};

const formatMoney = (item) =>
  typeof item === "string" || typeof item === "number"
    ? `$ ${Number(item).toFixed(2)}`
    : item || "";

const getLimitedLicenseColumns = (
  selectedRow,
  handleRadioChange,
  params,
  setParams,
  blockedLimitedCardNo,
  isSystemUser,
) => {
  const modParams = params ?? {};

  return [
    {
      title: "No",
      dataIndex: "LimitedCardNo",
      key: "LimitedCardNo",
      hidden: !isSystemUser,
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="LimitedCardNo"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "LimitedCardNo",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
    },
    {
      title: "Name",
      dataIndex: "LimitedCardName",
      key: "LimitedCardName",
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="LimitedCardName"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "LimitedCardName",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
    },
    {
      title: "License Item",
      dataIndex: "LicenseItem",
      key: "LicenseItem",
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="LicenseItem"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "LicenseItem",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
      render: (value) => LICENSE_ITEM_LABELS[Number(value)] || value || "",
    },
    {
      title: "License Code",
      dataIndex: "LicenseCode",
      key: "LicenseCode",
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="LicenseCode"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "LicenseCode",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
    },
    {
      title: "Term",
      dataIndex: "Term",
      key: "Term",
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="Term"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "Term",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
      render: (value) => TERM_LABELS[value] || value,
    },
    {
      title: "Price",
      dataIndex: "Price",
      key: "Price",
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="Price"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "Price",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
      render: (item) => formatMoney(item),
    },
    {
      title: "",
      key: "select",
      width: 50,
      align: "center",
      render: (_, record) => {
        const isBlockedRow =
          blockedLimitedCardNo != null &&
          Number(record.LimitedCardNo) === Number(blockedLimitedCardNo);
        return (
          <Radio
            checked={selectedRow?.LimitedCardNo === record.LimitedCardNo}
            onChange={() => handleRadioChange(record)}
            disabled={isBlockedRow}
          />
        );
      },
    },
  ];
};

export const LimitedLicenseForm = forwardRef(
  (
    {
      currentLimitedCardNo = null,
      onAlreadyAppliedChange,
      dealerDiscountPercent = 0,
      entityLabel = "site",
      limitedOrderStartDate = null,
      limitedOrderEndDate = null,
    },
    ref,
  ) => {
    const loggedInUser = JSON.parse(sessionStorage.getItem("userData") || "{}");
    const isSystemUser = [1, 2, 3].includes(loggedInUser?.role_value);

    const [listData, setListData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [filter, setFilter] = useState(initialFilterState);
    const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    const [params, setParams] = useState({
      limit: 10,
      page: 1,
      sort_by: "LimitedCardNo",
      order: "asc",
    });

    const query = new URLSearchParams(params).toString();
    const url = `${process.env.REACT_APP_API_URL}/api/limited/cards/?${query}`;
    const { data, loading, refetch } = useGet(url);

    const normalizedCurrentCardNo =
      currentLimitedCardNo != null && currentLimitedCardNo !== ""
        ? Number(currentLimitedCardNo)
        : null;
    const hasCurrentCardNo =
      Number.isFinite(normalizedCurrentCardNo) && normalizedCurrentCardNo > 0;

    const matchedCurrentCard = hasCurrentCardNo
      ? (listData.find(
          (row) => Number(row.LimitedCardNo) === normalizedCurrentCardNo,
        ) ?? null)
      : null;

    // Block matching LimitedCardNo while license is still running (or when dates are missing)
    const blockedLimitedCardNo =
      matchedCurrentCard == null
        ? null
        : limitedOrderStartDate == null ||
            isLimitedLicenseRunning({
              startDate: limitedOrderStartDate,
              endDate: limitedOrderEndDate,
              termMonths: matchedCurrentCard.Term,
            })
          ? normalizedCurrentCardNo
          : null;

    const activeBlockedCard =
      blockedLimitedCardNo != null ? matchedCurrentCard : null;

    const alreadyApplied =
      blockedLimitedCardNo != null &&
      selectedRow != null &&
      Number(selectedRow.LimitedCardNo) === Number(blockedLimitedCardNo);

    const paginationConfig = {
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: data?.count ?? data?.total ?? pagination.total,
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, 100],
      showTotal: (total, range) =>
        `Total ${total} items ${range[0]}-${range[1]}`,
      onChange: (page, pageSize) => {
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize: pageSize,
        }));
        setParams((prev) => ({
          ...prev,
          limit: pageSize,
          page: page,
        }));
      },
    };

    useImperativeHandle(
      ref,
      () => ({
        getSelectedRows: () => (selectedRow ? [selectedRow] : []),
        getCardByLimitedCardNo: (limitedCardNo) => {
          const n = Number(limitedCardNo);
          if (!Number.isFinite(n)) return null;
          return (
            listData.find((row) => Number(row.LimitedCardNo) === n) ?? null
          );
        },
        getCardByTerm: (term) => {
          const t = Number(term);
          if (!Number.isFinite(t)) return null;
          return listData.find((row) => Number(row.Term) === t) ?? null;
        },
        isAlreadyApplied: () => alreadyApplied,
      }),
      [selectedRow, alreadyApplied, listData],
    );

    useEffect(() => {
      onAlreadyAppliedChange?.(alreadyApplied);
    }, [alreadyApplied, onAlreadyAppliedChange]);

    const handleRadioChange = (record) => {
      if (
        blockedLimitedCardNo != null &&
        Number(record.LimitedCardNo) === Number(blockedLimitedCardNo)
      ) {
        return;
      }
      setSelectedRow(record);
    };

    useEffect(() => {
      if (data && !loading) {
        const newData = (data?.data ?? []).map((el, index) => ({
          ...el,
          key: el?.LimitedCardNo ?? el?.id ?? index,
        }));
        setListData(newData);
        const totalCount = data?.count ?? data?.total;
        if (typeof totalCount === "number") {
          setPagination((prev) => ({ ...prev, total: totalCount }));
        }

        // Pre-select current LimitedCardNo row so the active license is visible
        if (hasCurrentCardNo) {
          const match = newData.find(
            (el) => Number(el.LimitedCardNo) === normalizedCurrentCardNo,
          );
          if (match) {
            setSelectedRow((prev) => {
              if (
                prev &&
                Number(prev.LimitedCardNo) !== normalizedCurrentCardNo
              ) {
                return prev;
              }
              return prev?.LimitedCardNo === match.LimitedCardNo ? prev : match;
            });
          }
        }
      }
    }, [data, loading, hasCurrentCardNo, normalizedCurrentCardNo]);

    const handleFilter = () => {
      const filterDict = {};
      for (const key in filter) {
        if (
          filter[key] !== null &&
          filter[key] !== "" &&
          filter[key] !== undefined
        ) {
          filterDict[key] = filter[key];
        }
      }
      const isEmpty = JSON.stringify(filterDict) === "{}";
      if (isEmpty) {
        return;
      }
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams((prev) => ({
        ...prev,
        ...filterDict,
        page: 1,
      }));
      refetch();
    };

    const handleReset = () => {
      setFilter(initialFilterState);
      setPagination({
        current: 1,
        pageSize: 10,
        total: 0,
      });
      setParams({
        limit: 10,
        page: 1,
        sort_by: "LimitedCardNo",
        order: "asc",
      });
      refetch();
    };

    return (
      <div className="mt-3">
        <div className="mt-3">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
              <Input
                className="input_text_3 mr10 h-full"
                placeholder="Name"
                value={filter.LimitedCardName}
                onChange={(e) =>
                  setFilter((prev) => ({
                    ...prev,
                    LimitedCardName: e.target.value,
                  }))
                }
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                allowClear
                showSearch
                placeholder="License Item"
                optionFilterProp="label"
                options={LICENSE_ITEM_OPTIONS}
                value={filter.LicenseItem}
                onChange={(value) =>
                  setFilter((prev) => ({
                    ...prev,
                    LicenseItem: value != null ? Number(value) : value,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
              <Input
                className="input_text_3 mr10 h-full"
                placeholder="License Code"
                value={filter.LicenseCode}
                onChange={(e) => {
                  const { value } = e.target;
                  if (/^[1-9]\d*$/.test(value) || value === "") {
                    setFilter((prev) => ({
                      ...prev,
                      LicenseCode: value,
                    }));
                  }
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                allowClear
                placeholder="Term"
                options={TERM_OPTIONS}
                value={filter.Term}
                onChange={(value) =>
                  setFilter((prev) => ({
                    ...prev,
                    Term: value != null ? Number(value) : value,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Col>
          </Row>
        </div>

        <div className="flex items-center my-4 flex-nowrap gap-2">
          <Button
            type="primary"
            className="mr-2 w-[104px] h-[30px] font-medium"
            onClick={handleFilter}
          >
            <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
            Apply
          </Button>
          <Button
            className="reset-button w-[104px] h-[30px] font-medium"
            onClick={handleReset}
          >
            <HiArrowPath className="!text-[14px] font-medium" />
            Reset
          </Button>
        </div>

        {alreadyApplied ? (
          <div className="mb-3 text-xs text-[#003a8c] bg-[#e6f7ff] border border-[#91d5ff] rounded px-3 py-2">
            {`This ${entityLabel} already has Limited License - "${
              activeBlockedCard?.LimitedCardName || "-"
            }". Select a different item.`}
          </div>
        ) : null}

        <GlobalTable
          columns={getLimitedLicenseColumns(
            selectedRow,
            handleRadioChange,
            params,
            setParams,
            blockedLimitedCardNo,
            isSystemUser,
          ).filter((col) => !col.hidden)}
          dataSource={listData}
          pagination={paginationConfig}
          loading={loading}
        />
      </div>
    );
  },
);
